import React, { useCallback } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Avatar from '@components/dataDisplay/Avatar';
import { RootPath } from '@routes/routePath';

const Circle = styled.div<{
  size?: string;
  mobileSize?: string;
  clickable?: boolean;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 100%;
  border: 1px solid var(--primaryColor);
  width: ${({ size }) => size || '70px'};
  height: ${({ size }) => size || '70px'};
  flex-shrink: 0;
  cursor: ${({ clickable }) =>
    clickable ? 'var(--hoverCursorURL), pointer' : 'default'};
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: ${({ clickable }) => (clickable ? 'scale(1.05)' : 'none')};
  }

  .avatar {
    border-radius: 100px;
    overflow: hidden;
  }

  @media (max-width: 480px) {
    width: ${({ mobileSize }) => mobileSize || '50px'};
    height: ${({ mobileSize }) => mobileSize || '50px'};
    border: transparent;
  }
`;

const EmptyCircle = styled.div<{
  size?: string;
  mobileSize?: string;
}>`
  width: ${({ size }) => size || '60px'};
  height: ${({ size }) => size || '60px'};
  background: var(--backgroundColor);
  border-radius: 100px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 10px;
  color: var(--subTextColor);

  @media (max-width: 480px) {
    width: ${({ mobileSize }) => mobileSize || '40px'};
    height: ${({ mobileSize }) => mobileSize || '40px'};
  }
`;

export type CircleAvatarLinkProps = {
  /** 사용자 UID - 클릭 시 /user/{uid}/userDetail로 이동 */
  uid?: string | null;
  /** 아바타 이미지 URL */
  photoURL?: string | null;
  /** 원형 테두리 크기 (기본값: 70px) */
  size?: string;
  /** 모바일 원형 테두리 크기 (기본값: 50px) */
  mobileSize?: string;
  /** 아바타 이미지 크기 (기본값: 60px) */
  avatarSize?: string;
  /** 모바일 아바타 이미지 크기 (기본값: 40px) */
  avatarMobileSize?: string;
  /** 빈 상태 메시지 (기본값: 캐릭터 없음) */
  emptyText?: string;
  /** 클릭 이벤트 핸들러 (기본 네비게이션 외 추가 동작) */
  onClick?: (e: React.MouseEvent) => void;
  /** 네비게이션 비활성화 */
  disableNavigation?: boolean;
  /** 추가 스타일 */
  style?: React.CSSProperties;
  /** 추가 className */
  className?: string;
};

function CircleAvatarLink({
  uid,
  photoURL,
  size = '70px',
  mobileSize = '50px',
  avatarSize = '60px',
  avatarMobileSize = '40px',
  emptyText = '캐릭터 없음',
  onClick,
  disableNavigation = false,
  style,
  className,
}: CircleAvatarLinkProps) {
  const navigate = useNavigate();

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      if (onClick) {
        onClick(e);
      }

      if (!disableNavigation && uid) {
        navigate(`${RootPath.User}/${uid}/${RootPath.UserDetail}`);
      }
    },
    [uid, onClick, disableNavigation, navigate],
  );

  const isClickable = !disableNavigation && !!uid;
  // 📍 이미지 표시 조건: photoURL만 있으면 표시 (uid는 클릭 네비게이션에만 필요)
  const hasAvatar = !!photoURL;

  return (
    <Circle
      size={size}
      mobileSize={mobileSize}
      clickable={isClickable}
      onClick={handleClick}
      style={style}
      className={className}
    >
      {hasAvatar ? (
        <Avatar
          photoURL={photoURL || ''}
          size={avatarSize}
          mobileSize={avatarMobileSize}
          round
        />
      ) : (
        <EmptyCircle size={avatarSize} mobileSize={avatarMobileSize}>
          {emptyText}
        </EmptyCircle>
      )}
    </Circle>
  );
}

export default React.memo(CircleAvatarLink);
